import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Palette, Share2, Sparkles, Trophy, Globe } from 'lucide-react';

const services = [
  {
    id: 'branding',
    title: 'Branding & Identity',
    description: 'Logo design, visual identity systems, brand guidelines, and complete brand strategy for businesses ready to stand out.',
    icon: Palette,
  },
  {
    id: 'social-media',
    title: 'Digital Design',
    description: 'Scroll-stopping content, templates, stories, and cohesive feeds that build your brand presence across platforms.',
    icon: Share2,
  },
  {
    id: 'creative',
    title: 'Graphic Design',
    description: 'Custom visuals, marketing materials, presentations, and creative assets tailored to your unique project needs.',
    icon: Sparkles,
  },
  {
    id: 'web-design',
    title: 'Web Design & Development',
    description: 'Business websites, portfolios, landing pages, and digital layouts designed to convert visitors into clients.',
    icon: Globe,
  },
  {
    id: 'sports',
    title: 'Sports Design',
    description: 'Game day graphics, athlete branding, team visuals, and dynamic sports content that captures the energy of competition.',
    icon: Trophy,
  },
];

const ServiceCard = ({ service, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const Icon = service.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      data-testid={`service-card-${service.id}`}
      className="group card-hover bg-card border border-border/50 rounded-lg p-8 relative overflow-hidden"
    >
      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      <div className="relative z-10">
        {/* Icon */}
        <div className="service-icon mb-6">
          <Icon className="w-6 h-6" />
        </div>

        {/* Title */}
        <h3 className="font-['Sora'] text-xl font-semibold text-foreground mb-3">
          {service.title}
        </h3>

        {/* Description */}
        <p className="text-muted-foreground text-sm leading-relaxed">
          {service.description}
        </p>
      </div>
    </motion.div>
  );
};

export const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 px-6 lg:px-8 bg-card/30"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-primary text-sm font-medium tracking-wider uppercase">
            Services
          </span>
          <h2
            data-testid="services-title"
            className="font-['Sora'] text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mt-2 tracking-tight"
          >
            What I Offer
          </h2>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Comprehensive design services to elevate your brand across every touchpoint.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {services.map((service, index) => (
            <ServiceCard key={service.id} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
